import {
  Outlet,
  ScrollRestoration,
  createBrowserRouter,
  type RouteObject,
  Navigate,
} from 'react-router-dom';

import { Route } from './Route';
import * as P from '@/constants/paths';
import { SearchBar } from '@/components/shared/SearchBar';
import { ErrorBoundaryWrapper } from '@/components/ErrorBoundary/ErrorBoundaryWrapper';

import { Login } from '@/components/screens/Login/Login';
import { Landing } from '@screens/Landing';
import { MyFoodsPage } from '@screens/MyFoodsPage';
import { NutritionPage } from '@screens/NutritionPage';
import { PasswordRecovery } from '@screens/PasswordRecovery';
import { EmailConfirmation } from '@screens/EmailConfirmation';
import { ResetPassword } from '@screens/registration/ResetPassword';
import { ResetPasswordSuccess } from '@screens/registration/ResetPasswordSuccess';
import { ResetPasswordExpired } from '@screens/registration/ResetPasswordExpired';
import { MyIngredientsPage } from '@screens/MyIngredientsPage';
import { SettingsPage } from '@/components/screens/SettingsPage';
import { IngredientDetailPage } from '@screens/IngredientDetailPage';
import { SampleDetailsPage } from '@/components/screens/SampleDetailsPage';
import { SendMyReportsPage } from '@/components/screens/SendMyReportsPage';
import { MyHealthHistoryPage } from '@/components/screens/MyHealthHistoryPage';
import { AccountManagementPage } from '@/components/screens/AccountManagementPage';
import { TrackSamplesStatusPage } from '@/components/screens/TrackSamplesStatusPage';
import { NutritionFoodDetailPage } from '@screens/MyFoodsDetailPage/MyFoodsDetailPage';
import { HealthPage } from '@screens/HealthPages/HealthPage';
import { HealthConnectionsPage } from '@screens/HealthConnections';
import { HealthConditionsPage } from '@screens/HealthConditions';
import { HealthConditionDetailPage } from '@screens/HealthConditionsDetails';
import { ActiveMicrobesPage } from '@screens/ActiveMicrobes';
import { L2DetailPage } from '@screens/LevelTwoDetail/L2DetailPage';
import { BioAgePage } from '@screens/BioAge';
import { InflammagingPage } from '@screens/Imflammaging';
import { GutMicrobiomePage } from '@screens/GutMicrobiome';
import { OralMicrobiomePage } from '@screens/OralMicrobiome/OralMicrobiomePage';
import { OralDybiosisPage } from '@screens/OralDybiosis';
import { GutDybiosisPage } from '@screens/GutDybiosis';
import { OralRichnessPage } from '@screens/OralRichness';
import { GutRichnessPage } from '@screens/GutRichness';
import { HealthContributingScore } from '@screens/HealthPages/HealthContributingScore';
import { HealthL3DetailsPage } from '@screens/HealthPages/HealthL3DetailsPage';
import { HealthL4DetailsPage } from '@screens/HealthPages/HealthL4DetailsPage';
import { ShopPage } from '@screens/ShopPage';
import { QuestionnairesPage } from '@screens/QuestionnairesPage';
import { QuestionnaireDetailsPage } from '@screens/QuestionnairesPage/QuestionnaireDetailsPage';
import { QuestionnaireQuestionsPage } from '@screens/QuestionnairesPage/QuestionnaireQuestionsPage';
import { QuestionnaireSuccess } from '@screens/QuestionnairesPage/QuestionnaireSuccess';
import { QuestionnaireCompleted } from '@screens/QuestionnairesPage/QuestionnaireCompleted';
import { RegistrationLanding } from '@screens/registration/RegistrationLanding';
import { RegistrationKitId } from '@screens/registration/RegistrationKitId';
import { RegistrationAccountCreation } from '@screens/registration/RegistrationAccountCreation';
import { RegistrationPersonalInformation } from '@screens/registration/RegistrationPersonalInformation';
import { RegistrationContactInformation } from '@screens/registration/RegistrationContactInformation';
import { RegistrationConfirmation } from '@screens/registration/RegistrationConfirmation';
import { RegistrationPendingKit } from '@screens/registration/RegistrationPendingKit';
import { RegistrationBundleInfo } from '@screens/registration/RegistrationBundleInfo';
import { RegistrationAddressConfirmation } from '@screens/registration/RegistrationAddressConfirmation';

import { DashboardLayout } from '@layouts/DashboardLayout';
import { ResultsReady } from '@screens/HomePage/ResultReady/ResultsReady';
import { BoostersAndBlockersPage } from '@screens/HomePage/MyBoosterandBlockerPage/BoostersandBlockersPage';
import { PlantVirusPage } from '@screens/HomePage/PlantVirusPage/PlantVirusPage';
import { BloodSugarResponse } from '@screens/HomePage/BloodSugarResponsePage/BloodSugarResponse';
import { MoodMemoryFocusPage } from '@screens/HomePage/MoodMemoryAndFocusPage/MoodMemoryFocusPage';
import { CollectSamplePage } from '@screens/HomePage/CollectSamplePage';
import {
  SMP,
  SMPOrderDetailsPage,
  SMPSkipOrderPage,
  SMPFutureOrdersPage,
  SMPOrderHistoryPage,
  SMPChangeAddressPage,
  SMPPaymentMethodsPage,
  SMPResumeSubscriptionPage,
  SMPReactivateSubscriptionPage,
  SMPOrderNowPage,
  SMPPauseSubscriptionPage,
  SMPAddRemoveProductsPage,
  SMPCancelSubscriptionPage,
  SMPDiscountCodePage,
} from '@/components/screens/SMP';
import { HealthL4ImproveMyScoreDetail } from '@screens/HealthPages/HealthL4ImproveMyScoreDetail';
import { MaintenancePage } from '@screens/MaintenancePage/MaintenancePage';

import { BootupGuard } from './guards/BootupGuard';
import { SignUpProvider } from '@/contexts/SignupContext';
import { Toaster } from '@/components/design-system/toaster';
import { CancerDetectPage } from '@screens/CancerDetectPage';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { BoosterBlockerDetailPage } from '@screens/HomePage/MoodMemoryAndFocusPage/BoostersAndBlockersDetailPage';
import { ProductDetailPage } from '@screens/ShopPage/components/ProductDetailPage';
import { AddOrRemoveProducts } from '@screens/AddOrRemoveProducts';
import { TriSynbioticDetailPage } from '@screens/HomePage/TriSynbiotic/TriSynbioticDetailPage';
import UserConsentScreen from '@/components/screens/registration/RegistrationUserConsent';
import { RecipesPage } from '@/components/screens/RecipesPage/RecipesPage';
import { WrappedPageV2 } from '@/components/screens/WrappedPage/WrappedPageV2';
import { OrderDetailPage } from '@/components/screens/OrderDetailPage';

const ROUTING_CONFIG: RouteObject[] = [
  {
    path: P.MAINTENANCE_PATH,
    element: <MaintenancePage />,
    errorElement: <ErrorLoadingPage hideNavBar errorType="error" />,
  },
  {
    element: (
      <ErrorBoundaryWrapper>
        <Route>
          <ScrollRestoration />
          <Outlet />
        </Route>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorLoadingPage hideNavBar errorType="error" />,
    children: [
      {
        path: P.LOGIN_PATH,
        element: <Login />,
      },
      {
        path: P.LANDING_PATH,
        element: <Landing />,
      },
    ],
  },
  {
    element: (
      <ErrorBoundaryWrapper>
        <Route fromRegistration>
          <ScrollRestoration />
          <SignUpProvider>
            <Toaster />
            <Outlet />
          </SignUpProvider>
        </Route>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorLoadingPage hideNavBar errorType="error" />,
    children: [
      // Registration
      {
        path: P.REGISTRATION_LANDING,
        element: <RegistrationLanding />,
      },
      {
        path: `${P.REGISTRATION_LANDING}/welcome`,
        element: <Navigate to={P.REGISTRATION_LANDING} />,
      },
      {
        path: `${P.REGISTRATION_LANDING}/kit`,
        element: <Navigate to={P.REGISTRATION_KIT_ID} replace />,
      },
      {
        path: P.REGISTRATION_KIT_ID,
        element: <RegistrationKitId />,
      },
      {
        path: P.REGISTRATION_ACCOUNT_CREATION,
        element: <RegistrationAccountCreation />,
      },
      {
        path: P.REGISTRATION_PERSONAL_INFORMATION,
        element: <RegistrationPersonalInformation />,
      },
      {
        path: P.REGISTRATION_CONTACT_INFORMATION,
        element: <RegistrationContactInformation />,
      },
      {
        path: P.REGISTRATION_CONFIRMATION,
        element: <RegistrationConfirmation />,
      },
      {
        path: P.REGISTRATION_PENDING_KIT,
        element: <RegistrationPendingKit />,
      },
      {
        path: P.REGISTRATION_BUNDLE_INFO,
        element: <RegistrationBundleInfo />,
      },
      {
        path: P.REGISTRATION_ADDRESS_CONFIRMATION,
        element: <RegistrationAddressConfirmation />,
      },
      {
        path: P.REGISTRATION_USER_CONSENT,
        element: <UserConsentScreen />,
      },
      {
        path: P.PASSWORD_RECOVERY_PATH,
        element: <PasswordRecovery />,
      },
      {
        path: P.RESET_PASSWORD_PATH,
        element: <ResetPassword />,
      },
      {
        path: P.RESET_PASSWORD_SUCCESS_PATH,
        element: <ResetPasswordSuccess />,
      },
      {
        path: P.RESET_PASSWORD_EXPIRED_PATH,
        element: <ResetPasswordExpired />,
      },
      {
        path: P.EMAIL_CONFIRMATION_PATH,
        element: <EmailConfirmation />,
      },
    ],
  },
  {
    element: (
      <ErrorBoundaryWrapper>
        <Route isProtected>
          <ScrollRestoration />
          <DashboardLayout />
        </Route>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorLoadingPage hideNavBar errorType="error" />,
    children: [
      {
        element: (
          <BootupGuard>
            <SearchBar>
              <Outlet />
            </SearchBar>
          </BootupGuard>
        ),
        errorElement: <ErrorLoadingPage hideNavBar errorType="error" />,
        children: [
          {
            path: P.HOME_PATH,
            element: <ResultsReady />,
          },
          {
            path: P.MY_FOODS_PATH,
            element: <MyFoodsPage />,
          },
          {
            path: P.RECIPES_PATH,
            element: <RecipesPage />,
          },
          {
            path: P.WRAPPED_V2_PATH,
            element: <WrappedPageV2 />,
          },
          {
            path: P.NUTRITION_PATH,
            element: <NutritionPage />,
          },
          {
            path: P.FOOD_DETAILS_PATH,
            element: <NutritionFoodDetailPage />,
          },
          {
            path: P.FORMULA_PATH,
            element: <MyIngredientsPage />,
          },
          {
            path: P.INGREDIENT_DETAILS_PATH,
            element: <IngredientDetailPage />,
          },
          {
            path: P.HEALTH_PATH,
            element: <HealthPage />,
          },
          {
            path: P.HEALTH_CONNECTIONS_PATH,
            element: <HealthConnectionsPage />,
          },
          {
            path: P.HEALTH_CONDITIONS_PATH,
            element: <HealthConditionsPage />,
          },
          {
            path: P.HEALTH_CONDITION_DETAIL_PATH,
            element: <HealthConditionDetailPage />,
          },
          {
            path: P.ACTIVE_MICROBES_PATH,
            element: <ActiveMicrobesPage />,
          },
          {
            path: P.L2_SCORE_PATH,
            element: <L2DetailPage />,
          },
          {
            path: P.HEALTH_CONTRIBUTING_SCORE,
            element: <HealthContributingScore />,
          },
          {
            path: P.L2_CONTRIBUTING_SCORE,
            element: <HealthContributingScore />,
          },
          {
            path: P.L3_DETAIL_PATH,
            element: <HealthL3DetailsPage />,
          },
          {
            path: P.L4_DETAIL_PATH,
            element: <HealthL4DetailsPage />,
          },
          {
            path: P.BIO_AGE_PATH,
            element: <BioAgePage />,
          },
          {
            path: P.INFLAMMAGING_PATH,
            element: <InflammagingPage />,
          },
          {
            path: P.GUT_MICROBIOME_PATH,
            element: <GutMicrobiomePage />,
          },
          {
            path: P.ORAL_MICROBIOME_PATH,
            element: <OralMicrobiomePage />,
          },
          {
            path: P.GUT_RICHNESS_PATH,
            element: <GutRichnessPage />,
          },
          {
            path: P.GUT_DYBIOSIS_PATH,
            element: <GutDybiosisPage />,
          },
          {
            path: P.ORAL_RICHNESS_PATH,
            element: <OralRichnessPage />,
          },
          {
            path: P.ORAL_DYBIOSIS_PATH,
            element: <OralDybiosisPage />,
          },
          {
            path: P.IMPROVE_MY_SCORE_DETAIL,
            element: <HealthL4ImproveMyScoreDetail />,
          },
          {
            path: P.RESULTS_READY_PATH,
            element: <ResultsReady />,
          },
          {
            path: P.BOOSTERS_AND_BLOCKERS_PATH,
            element: <BoostersAndBlockersPage />,
          },
          {
            path: P.PLANT_VIRUS_PATH,
            element: <PlantVirusPage />,
          },
          {
            path: P.BLOOD_SUGAR_PATH,
            element: <BloodSugarResponse />,
          },
          {
            path: P.MOOD_MEMORY_FOCUS_PATH,
            element: <MoodMemoryFocusPage />,
          },
          {
            path: P.COLLECT_SAMPLE_PATH,
            element: <CollectSamplePage />,
          },
          {
            path: P.TRISYNBIOTIC_DETAIL_PATH,
            element: <TriSynbioticDetailPage />,
          },
          {
            path: P.SHOP_PATH,
            element: <ShopPage />,
          },
          {
            path: P.ADD_OR_REMOVE_PRODUCTS,
            element: <AddOrRemoveProducts />,
          },
          {
            path: P.QUESTIONNAIRES_PATH,
            element: <QuestionnairesPage />,
          },
          {
            path: P.QUESTIONNAIRE_DETAILS_PATH,
            element: <QuestionnaireDetailsPage />,
          },
          {
            path: P.QUESTIONNAIRE_QUESTIONS_PATH,
            element: <QuestionnaireQuestionsPage />,
          },
          {
            path: P.QUESTIONNAIRE_SUCCESS_PATH,
            element: <QuestionnaireSuccess />,
          },
          {
            path: P.QUESTIONNAIRE_COMPLETED_PATH,
            element: <QuestionnaireCompleted />,
          },
          {
            path: P.SMP_PATH,
            element: <SMP />,
          },
          {
            path: P.SMP_ORDER_DETAILS_PATH,
            element: <SMPOrderDetailsPage />,
          },
          {
            path: P.SMP_SKIP_NEXT_ORDER_PATH,
            element: <SMPSkipOrderPage />,
          },
          {
            path: P.SMP_FUTURE_ORDERS_PATH,
            element: <SMPFutureOrdersPage />,
          },
          {
            path: P.SMP_ORDER_HISTORY_PATH,
            element: <SMPOrderHistoryPage />,
          },
          {
            path: P.SMP_ORDER_HISTORY_DETAILS_PATH,
            element: <SMPOrderDetailsPage />,
          },
          {
            path: P.SMP_CHANGE_ADDRESS_PATH,
            element: <SMPChangeAddressPage />,
          },
          {
            path: P.SMP_PAYMENT_METHODS_PATH,
            element: <SMPPaymentMethodsPage />,
          },
          {
            path: P.SMP_RESUME_SUBSCRIPTION_PATH,
            element: <SMPResumeSubscriptionPage />,
          },
          {
            path: P.SMP_REACTIVATE_PATH,
            element: <SMPReactivateSubscriptionPage />,
          },
          {
            path: P.SMP_I_NEED_IT_SOONER_PATH,
            element: <SMPOrderNowPage />,
          },
          {
            path: P.SMP_PAUSE_SUBSCRIPTION_PATH,
            element: <SMPPauseSubscriptionPage />,
          },
          {
            path: P.SMP_ADD_REMOVE_PRODUCTS_PATH,
            element: <SMPAddRemoveProductsPage />,
          },
          {
            path: P.SMP_CANCEL_SUBSCRIPTION_PATH,
            element: <SMPCancelSubscriptionPage />,
          },
          {
            path: P.SMP_DISCOUNT_CODE_PATH,
            element: <SMPDiscountCodePage />,
          },
          {
            path: P.CANCER_DETECT_PATH,
            element: <CancerDetectPage />,
          },
          {
            path: P.ACCOUNT_MANAGEMENT_PATH,
            element: <AccountManagementPage />,
          },
          {
            path: P.SETTINGS_PATH,
            element: <SettingsPage />,
          },
          {
            path: P.MY_HEALTH_HISTORY_PATH,
            element: <MyHealthHistoryPage />,
          },
          {
            path: P.TRACK_SAMPLE_STATUS_PATH,
            element: <TrackSamplesStatusPage />,
          },
          {
            path: P.SAMPLE_DETAILS_PATH,
            element: <SampleDetailsPage />,
          },
          {
            path: P.MY_REPORTS_PATH,
            element: <SendMyReportsPage />,
          },
          {
            path: P.BOOSTER_BLOCKER_DETAIL,
            element: <BoosterBlockerDetailPage />,
          },
          {
            path: P.PRODUCT_DETAIL_PAGE_PATH,
            element: <ProductDetailPage />,
          },
          {
            path: P.ORDER_DETAIL_PATH,
            element: <OrderDetailPage />,
          },
        ],
      },
      {
        path: '*',
        element: <ErrorLoadingPage hideNavBar errorType="notFound" />,
      },
    ],
  },
];

export const router = createBrowserRouter(ROUTING_CONFIG);
